/** @jsxImportSource @emotion/react */
import React, { useContext } from 'react';
import BigNumber from 'bignumber.js';

import { AuthContext } from 'context/AuthContext';
import useSuccessfulTransactionModal from 'hooks/useSuccessfulTransactionModal';
import { useGetBrnReward, useClaimBrnReward } from 'clients/api';
import { useTranslation } from 'translation';
import { TokenId } from 'types';
import { VError } from 'errors';
import useConvertWeiToReadableTokenString from 'hooks/useConvertWeiToReadableTokenString';
import { transactionErrorPhrases } from 'errors/transactionErrorPhrases';
import { toast } from '../../Toast';
import { Icon } from '../../Icon';
import { SecondaryButton, IButtonProps } from '../../Button';
import { useStyles } from './styles';

const BRN_SYMBOL = 'brn';

export const TEST_ID = 'claim-brn-reward-button';

export interface IClaimBrnRewardButton extends Omit<IButtonProps, 'onClick'> {
  onClaim: () => Promise<string | undefined>;
  amountWei?: BigNumber;
}

export const ClaimBrnRewardButtonUi: React.FC<IClaimBrnRewardButton> = ({
  amountWei,
  onClaim,
  ...otherProps
}) => {
  const { t, Trans } = useTranslation();
  const styles = useStyles();

  const { openSuccessfulTransactionModal } = useSuccessfulTransactionModal();

  const readableAmount = useConvertWeiToReadableTokenString({
    valueWei: amountWei,
    tokenId: BRN_SYMBOL,
    minimizeDecimals: true,
  });

  // Check readable amount isn't 0 (since we strip out decimals)
  if (!amountWei || readableAmount.split(' ')[0] === '0') {
    return null;
  }

  const handleClick = async () => {
    try {
      const transactionHash = await onClaim();
      if (transactionHash) {
        // Display successful transaction modal
        openSuccessfulTransactionModal({
          title: t('claimBrnRewardButton.successfulTransactionModal.title'),
          content: t('claimBrnRewardButton.successfulTransactionModal.message'),
          amount: {
            valueWei: amountWei,
            tokenId: 'brn' as TokenId,
          },
          transactionHash,
        });
      }
    } catch (error) {
      let { message } = error as Error;
      if (error instanceof VError && error.type === 'transactions') {
        message = transactionErrorPhrases[error.message as keyof typeof transactionErrorPhrases];
      }
      toast.error({
        message,
      });
    }
  };

  return (
    <SecondaryButton
      data-testid={TEST_ID}
      css={styles.button}
      onClick={handleClick}
      {...otherProps}
    >
      <Trans
        i18nKey="claimBrnRewardButton.title"
        components={{
          Icon: <Icon css={styles.icon} name={BRN_SYMBOL} />,
        }}
        values={{
          amount: readableAmount,
        }}
      />
    </SecondaryButton>
  );
};

export const ClaimBrnRewardButton: React.FC<IButtonProps> = props => {
  const { account } = useContext(AuthContext);
  const { data: brnRewardWei } = useGetBrnReward(account?.address);

  const { mutateAsync: claimBrnReward, isLoading: isClaimBrnRewardLoading } = useClaimBrnReward();

  const handleClaim = async () => {
    if (!account?.address) {
      throw new VError({ type: 'unexpected', code: 'walletNotConnected' });
    }

    const res = await claimBrnReward({
      fromAccountAddress: account.address,
    });
    return res.transactionHash;
  };

  return (
    <ClaimBrnRewardButtonUi
      amountWei={brnRewardWei}
      loading={isClaimBrnRewardLoading}
      onClaim={handleClaim}
      {...props}
    />
  );
};

export default ClaimBrnRewardButton;
